import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/journey", label: "Journey" },
  { to: "/book", label: "The Book" },
  { to: "/awards", label: "Awards" },
  { to: "/franchise", label: "Franchise" },
  { to: "/press", label: "Press" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color] duration-500 border-b ${
        scrolled ? "glass border-white/10" : "bg-transparent border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <span className="w-9 h-9 rounded-full bg-brand flex items-center justify-center font-display font-semibold text-white text-lg leading-none group-hover:scale-110 transition-transform duration-300">
            GP
          </span>
          <span className="font-display text-xl tracking-tight text-paper hidden sm:block">
            Gurpreet Paul
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-6" data-testid="nav-desktop">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] uppercase tracking-[0.18em] font-body transition-colors duration-300 hover:text-brand ${
                  isActive ? "text-brand" : "text-white/60"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <Link
            to="/book"
            data-testid="nav-cta-book"
            className="ml-2 inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-[12px] uppercase tracking-[0.15em] text-white hover:bg-brand-dark transition-colors duration-300"
          >
            Read the Book <ArrowUpRight size={14} />
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="md:hidden text-paper p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden glass border-t border-white/10 overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-mobile-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `font-display text-2xl tracking-tight ${isActive ? "text-brand" : "text-paper"}`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
